import { useMemo } from "react";

import { typedQueryClient } from "hooks";
import { DealersDashboardQueryKeys } from "modules/DealersDashboard/queryKeys";

import type { WebSocketMessageListener } from "components";
import type { Dealer, DealerLocation } from "models";

export const useDealersDashboardListeners = () =>
  useMemo(() => {
    const dealerListeners: WebSocketMessageListener<Dealer>[] = [
      {
        model: "Dealer",
        action: "upsert",
        callback: ({ data: dealer }) => {
          const dealers = typedQueryClient.getQueryData(DealersDashboardQueryKeys.list);
          if (!dealers) return;

          const dealerIdx = dealers.findIndex(d => d.id === dealer.id);

          typedQueryClient.setQueryData(
            DealersDashboardQueryKeys.list,
            dealerIdx === -1 ? [...dealers, dealer] : dealers.with(dealerIdx, { ...dealers[dealerIdx], ...dealer })
          );
        }
      },
      {
        model: "Dealer",
        action: "delete",
        callback: ({ data: dealer }) => {
          const dealers = typedQueryClient.getQueryData(DealersDashboardQueryKeys.list);
          if (!dealers?.length) return;

          const dealerIdx = dealers.findIndex(d => d.id === dealer.id);
          if (dealerIdx === -1) return;

          typedQueryClient.setQueryData(DealersDashboardQueryKeys.list, dealers.toSpliced(dealerIdx, 1));
        }
      }
    ];

    const locationListeners: WebSocketMessageListener<DealerLocation>[] = [
      {
        model: "DealerLocation",
        action: "upsert",
        callback: ({ data: location }) => {
          const dealers = typedQueryClient.getQueryData(DealersDashboardQueryKeys.list);
          if (!dealers?.length) return;

          const dealerIdx = dealers.findIndex(dealer => dealer.id === location.dealer_id);
          if (dealerIdx === -1) return;

          const dealer = dealers[dealerIdx];
          const locations = dealer.locations ?? [];
          const locationIdx = locations.findIndex(l => l.id === location.id);

          typedQueryClient.setQueryData(
            DealersDashboardQueryKeys.list,
            dealers.with(dealerIdx, {
              ...dealer,
              locations: locationIdx === -1 ? [...locations, location] : locations.with(locationIdx, { ...locations[locationIdx], ...location })
            })
          );
        }
      },
      {
        model: "DealerLocation",
        action: "delete",
        callback: ({ data: location }) => {
          const dealers = typedQueryClient.getQueryData(DealersDashboardQueryKeys.list);
          if (!dealers?.length) return;

          const dealerIdx = dealers.findIndex(dealer => dealer.id === location.dealer_id);
          if (dealerIdx === -1) return;

          const dealer = dealers[dealerIdx];
          const locationIdx = dealer.locations?.findIndex(l => l.id === location.id) ?? -1;
          if (locationIdx === -1) return;

          typedQueryClient.setQueryData(DealersDashboardQueryKeys.list, dealers.with(dealerIdx, { ...dealer, locations: dealer.locations?.toSpliced(locationIdx, 1) }));
        }
      }
    ];

    return [...dealerListeners, ...locationListeners];
  }, []);
